import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Card, 
  Tabs, 
  Button, 
  Space, 
  message, 
  Spin,
  Typography,
  Alert,
  Form
} from 'antd';
import { 
  ApartmentOutlined, 
  TeamOutlined, 
  SaveOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import CMTStructureManager from './CMTStructureManager';
import CMTMemberManager from './CMTMemberManager';
import CrisisManagementTeam from '../onboarding/CrisisManagementTeam';
import cmtService from '../../services/cmtService';


const { Title } = Typography;
const { TabPane } = Tabs;

/**
 * 🎯 CMT Admin Interface
 * 
 * Main component that combines CMT structure and member management
 * with simple state management and save functionality.
 */
export default function CMTAdminInterface({ organizationData, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cmts, setCmts] = useState([]);
  const [members, setMembers] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  // Fresh org document state to avoid stale props after save
  const [orgDoc, setOrgDoc] = useState(null);
  const [form] = Form.useForm();
  
  // 🚨 CRITICAL FIX: Prevent form re-seeding
  const didInit = useRef(false);
  const lastOrgId = useRef(null);
  
  // Auto-save state
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const autoSaveTimeoutRef = useRef(null);
  
  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  /**
   * 🔄 Load initial data
   */
  useEffect(() => {
    loadData();
  }, [organizationData?.id]); // Only reload when org ID changes, not on every org data update

  const loadData = async (force = false) => {
    try {
      setLoading(true);
      
      if (!organizationData?.id) {
        console.error('❌ No organization ID available');
        return;
      }

      // 🚨 CRITICAL FIX: Don't reload data if user is actively editing (has unsaved changes)
      if (hasChanges && !force) {
        console.log('⚠️ PREVENTING DATA RELOAD: User has unsaved changes - protecting form state');
        setLoading(false);
        return;
      }

      console.log('📊 Loading CMT data for org:', organizationData.id);
      
      // 🚨 DEBUG: Check raw Firestore document for corrupted data
      try {
        const { getDoc, doc } = await import('firebase/firestore');
        const { db } = await import('../../config/firebase');
        const snap = await getDoc(doc(db, 'c3_organisations', organizationData.id));
        console.log('🔍 RAW ORG DOC:', JSON.stringify(snap.data(), null, 2));
      } catch (err) {
        console.error('Failed to fetch raw org doc:', err);
      }
      
      // Load CMT structure from /cmts subcollection
      const cmtStructure = await cmtService.getOrganizationCMTs(organizationData.id);
      setCmts(Array.isArray(cmtStructure) ? cmtStructure : []);
      
      // 🚨 CRITICAL FIX: Load fresh data from Firestore, not stale organizationData prop
      // Single source of truth: multiCmtMembers. Legacy cmtMembers only for fallback.
      let cmtMembers = [];
      let multiCmtMembers = {};
      
      try {
        const { getDoc, doc } = await import('firebase/firestore');
        const { db } = await import('../../config/firebase');
        const freshOrgSnap = await getDoc(doc(db, 'c3_organisations', organizationData.id));
        const freshOrgData = freshOrgSnap.data();
        setOrgDoc(freshOrgData || {});
        
        multiCmtMembers = freshOrgData?.multiCmtMembers || {};
        // Fallback only if multiCmtMembers is empty
        if (Object.keys(multiCmtMembers).length === 0) {
          cmtMembers = freshOrgData?.cmtMembers || [];
        }
        
        console.log('🔥 LOADED FRESH DATA FROM FIRESTORE:', {
          cmtMembersCount: cmtMembers.length,
          multiCmtMembersKeys: Object.keys(multiCmtMembers),
          freshOrgDataKeys: Object.keys(freshOrgData || {}),
          multiCmtMembersDetail: multiCmtMembers
        });
      } catch (freshDataError) {
        console.error('❌ Failed to load fresh data, falling back to prop:', freshDataError);
        // Fallback to prop data if fresh fetch fails
        multiCmtMembers = organizationData?.multiCmtMembers || {};
        if (Object.keys(multiCmtMembers).length === 0) {
          cmtMembers = organizationData?.cmtMembers || [];
        }
      }
      
      console.log('🔍 DEBUG - Member data sources:', {
        cmtMembers: cmtMembers.length,
        multiCmtMembersKeys: Object.keys(multiCmtMembers),
        organizationData: Object.keys(organizationData || {})
      });
      
      // Convert using centralized service (adds monitoring)
      let allMembers = cmtService.flattenMembersFromOrgDoc({ multiCmtMembers, cmtMembers });
      
      setMembers(Array.isArray(allMembers) ? allMembers : []);
      
      // 🚨 CRITICAL: Initialize form with existing member data organized by CMT
      // Using the EXACT structure that Form.List expects: { multiCmtMembers: { cmtId: [member1, member2] } }
      const formData = { multiCmtMembers: {} };
      cmtStructure.forEach(cmt => {
        const cmtId = cmt.id;
        const membersForCmt = allMembers.filter(member => member.cmtId === cmtId);
        formData.multiCmtMembers[cmtId] = membersForCmt.map(member => ({
          fullName: member.fullName || '',
          email: member.email || '',
          phone: member.phone || '',
          cmtRole: member.cmtRole || 'other'
        }));
      });
      
      console.log('🔧 Setting form data with existing members:', {
        formData,
        totalMembers: allMembers.length,
        membersByCmt: Object.fromEntries(Object.entries(formData.multiCmtMembers).map(([k,v]) => [k, v.length])),
        detailedFormData: formData,
        originalMultiCmtMembers: multiCmtMembers,
        originalCmtMembers: cmtMembers,
        allMembersDetailed: allMembers
      });
      
      // 🚨 DEBUG: Log what we're about to set in the form
      console.log('[SEED] formData about to setFieldsValue:', JSON.stringify(formData, null, 2));
      
      // 🚨 CRITICAL FIX: Gate form initialization to prevent re-seeding
      const orgIdChanged = lastOrgId.current !== organizationData.id;
      if (!didInit.current || orgIdChanged) {
        console.log('🔧 SEEDING FORM - First init or org changed:', { didInit: didInit.current, orgIdChanged });
        
        if (orgIdChanged) {
          form.resetFields(); // Clean slate for new org
          lastOrgId.current = organizationData.id;
        }
        
        form.setFieldsValue(formData);
        didInit.current = true;
      } else {
        console.log('⚠️ SKIPPING FORM SEED - Already initialized for this org');
      }
      
      // 🚨 FORCE FORM RENDER by triggering state update
      setTimeout(() => {
        console.log('🔍 Form values after set:', form.getFieldsValue());
      }, 100);
      setHasChanges(false);
      
      console.log('📊 CMT Admin Interface - Loaded data:', {
        cmts: cmtStructure.length,
        members: cmtMembers.length,
        formData,
        cmtStructure,
        cmtMembers
      });
      
    } catch (error) {
      console.error('❌ Error loading CMT data:', error);
      message.error('Failed to load CMT data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 🏗️ Handle structure changes
   */
  const handleStructureChange = (newCmts) => {
    setCmts(newCmts);
    setHasChanges(true);
    // 🚨 REMOVED triggerAutoSave() to prevent form reversion during active editing
    console.log('🏗️ CMT structure updated:', newCmts.length, 'CMTs');
  };

  /**
   * 👥 Handle member changes
   */
  const handleMembersChange = (newMembers) => {
    console.log('👥 CMT members updated - RAW:', newMembers);
    setMembers(newMembers);
    setHasChanges(true);
    // 🚨 REMOVED triggerAutoSave() to prevent email field reversion on keystroke
    console.log('👥 CMT members updated:', newMembers.length, 'members');
  };

  /**
   * 💾 Save all changes with automatic sync
   */
  const handleSave = async () => {
    try {
      setSaving(true);
      
      // 🚨 CRITICAL DEBUG: Get CURRENT form values before saving
      const currentFormValues = form.getFieldsValue();
      console.log('🔍 CURRENT FORM VALUES:', JSON.stringify(currentFormValues, null, 2));
      
      console.log('💾 MANUAL SAVE - About to save data:', {
        orgId: organizationData.id,
        cmtCount: cmts.length,
        memberCount: members.length,
        membersData: members,
        cmtsData: cmts.map(cmt => ({ id: cmt.id, name: cmt.name })),
        currentFormValues: currentFormValues
      });
      
      // 🚨 CRITICAL FIX: Extract members from CURRENT form values, not stale state
      const freshMembers = [];
      
      if (currentFormValues.multiCmtMembers) {
        Object.entries(currentFormValues.multiCmtMembers).forEach(([cmtId, cmtMembers]) => {
          if (Array.isArray(cmtMembers)) {
            cmtMembers.forEach(member => {
              // 🚨 CRITICAL FIX: Filter out null/undefined members and check for valid email
              if (member && member.email && member.email.trim()) {
                freshMembers.push({
                  ...member,
                  cmtId: cmtId
                });
              }
            });
          }
        });
      }
      
      console.log('🔄 FRESH MEMBERS FROM FORM:', JSON.stringify(freshMembers, null, 2));
      console.log('🔄 COMPARISON - State members vs Form members:', {
        stateCount: members.length,
        formCount: freshMembers.length,
        stateEmails: members.map(m => m.email),
        formEmails: freshMembers.map(m => m.email)
      });
      
      // Save CMT data using FRESH form values and get fresh server copy
      const freshServerDoc = await cmtService.saveCMTData(
        organizationData.id,
        cmts,
        freshMembers, // Use fresh form data instead of stale state
        'admin_user'
      );
      // Update local orgDoc and reseed form from server copy
      setOrgDoc(freshServerDoc || {});
      const reseedMulti = (freshServerDoc && freshServerDoc.multiCmtMembers) ? freshServerDoc.multiCmtMembers : {};
      form.resetFields();
      form.setFieldsValue({ multiCmtMembers: reseedMulti });
      
      // 🚨 Execute ROBUST Users sync using new server-side sync function
      try {
        console.log('🚀 EXECUTING ROBUST SYNC: About to call syncCmtMembers Cloud Function');
        
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions(undefined, 'australia-southeast2');
        const syncCmtMembersCallable = httpsCallable(functions, 'syncCmtMembers');
        
        // Build authoritative member list from fresh server data
        const freshMultiCmtMembers = freshServerDoc?.multiCmtMembers || {};
        
        // Execute sync for each CMT individually to ensure proper isolation
        const syncResults = [];
        for (const cmt of cmts) {
          const cmtMembers = freshMultiCmtMembers[cmt.id] || [];
          
          // 🚨 CRITICAL: Always sync every CMT, including empty ones!
          // Empty CMTs (0 members) need syncing to REMOVE users from database
          console.log(`🔄 Syncing ${cmtMembers.length} members for CMT: ${cmt.name} (${cmt.id})`);
          
          const result = await syncCmtMembersCallable({
            orgId: organizationData.id,
            cmtId: cmt.id,
            authoritativeMembers: cmtMembers
          });
          
          console.log(`✅ CMT ${cmt.name} sync completed:`, result.data);
          console.log(`🔍 DETAILED SYNC RESULTS for ${cmt.name}:`, {
            success: result.data.success,
            created: result.data.results?.created || 0,
            updated: result.data.results?.updated || 0,
            removed: result.data.results?.removed || 0,
            unchanged: result.data.results?.unchanged || 0,
            errors: result.data.results?.errors || [],
            message: result.data.message,
            fullResults: result.data.results
          });
          
          syncResults.push({
            cmtId: cmt.id,
            cmtName: cmt.name,
            result: result.data
          });
        }
        
        // Aggregate results with robust error handling
        const successfulResults = syncResults.filter(r => r.result.success);
        const failedResults = syncResults.filter(r => !r.result.success);
        
        const aggregatedResult = {
          success: syncResults.length > 0 && syncResults.every(r => r.result.success),
          totalCmts: syncResults.length,
          successfulCmts: successfulResults.length,
          failedCmts: failedResults.length,
          results: syncResults,
          totalCreated: successfulResults.reduce((sum, r) => sum + (r.result.results?.created || 0), 0),
          totalUpdated: successfulResults.reduce((sum, r) => sum + (r.result.results?.updated || 0), 0),
          totalRemoved: successfulResults.reduce((sum, r) => sum + (r.result.results?.removed || 0), 0),
          totalUnchanged: successfulResults.reduce((sum, r) => sum + (r.result.results?.unchanged || 0), 0),
          errors: failedResults.map(r => ({
            cmtId: r.cmtId,
            cmtName: r.cmtName,
            error: r.result.error
          })),
          // Create stats object for compatibility
          stats: {
            totalMembers: successfulResults.reduce((sum, r) => sum + (r.result.results?.created || 0) + (r.result.results?.updated || 0) + (r.result.results?.unchanged || 0), 0),
            usersCreated: successfulResults.reduce((sum, r) => sum + (r.result.results?.created || 0), 0),
            usersUpdated: successfulResults.reduce((sum, r) => sum + (r.result.results?.updated || 0), 0),
            errorsCount: failedResults.length
          }
        };
        
        console.log('✅ ROBUST SYNC COMPLETED:', aggregatedResult);
        setSyncResults(aggregatedResult);
        setLastSyncTime(new Date());
        
        if (aggregatedResult.success) {
          const successMsg = `Sync completed: ${aggregatedResult.totalCreated} created, ${aggregatedResult.totalUpdated} updated, ${aggregatedResult.totalRemoved} removed, ${aggregatedResult.totalUnchanged} unchanged`;
          message.success(successMsg);
        } else if (aggregatedResult.successfulCmts > 0) {
          const partialMsg = `Partial sync: ${aggregatedResult.successfulCmts}/${aggregatedResult.totalCmts} CMTs synced successfully. ${aggregatedResult.failedCmts} failed.`;
          message.warning(partialMsg);
        } else {
          const errorMsg = `Sync failed for all ${aggregatedResult.totalCmts} CMTs. Check permissions and try again.`;
          message.error(errorMsg);
        }
        
        // 🧹 ORGANIZATION CLEANUP: Remove users who are no longer in any CMT
        if (aggregatedResult.success || aggregatedResult.successfulCmts > 0) {
          try {
            console.log('🧹 Starting organization-wide user cleanup...');
            const cleanupCallable = httpsCallable(functions, 'cleanupOrganizationUsers', {
              region: 'australia-southeast2'
            });
            
            const cleanupResult = await cleanupCallable({
              orgId: organizationData.id
            });
            
            console.log('✅ Organization cleanup completed:', cleanupResult.data);
            
            if (cleanupResult.data.results.usersDeleted > 0) {
              message.success(`Cleanup completed: ${cleanupResult.data.results.usersDeleted} orphaned users removed`);
            } else {
              console.log('ℹ️ No orphaned users found - cleanup complete');
            }
            
          } catch (cleanupError) {
            console.error('❌ Organization cleanup failed:', cleanupError);
            message.warning('Sync completed but cleanup failed. Some orphaned users may remain.');
          }
        }
        
      } catch (syncError) {
        console.error('❌ Robust sync failed:', syncError);
        message.error(`Sync failed: ${syncError.message}`);
      }

      // 🚨 CRITICAL FIX: Force reload fresh data from Firestore after save
      console.log('🔄 FORCING DATA RELOAD AFTER SAVE...');
      didInit.current = false; // Reset initialization flag to force form re-seeding
      setHasChanges(false); // Clear changes so reload is not blocked
      await loadData(true); // Reload fresh data from Firestore, bypassing guard
      
      setHasChanges(false);
      setLastSaved(new Date());
      message.success('CMT configuration saved successfully!');
      
    } catch (error) {
      console.error('❌ Error saving CMT data:', error);
      message.error('Failed to save CMT configuration');
    } finally {
      setSaving(false);
    }
  };

  /**
   * 🔄 Handle Automatic Sync After Save
   */
  const handleAutoSync = async () => {
    try {
      console.log('🔄 AUTO SYNC: Triggering CMT → Users sync...');
      
      // Show sync in progress
      message.loading('Syncing CMT members to Users tab...', 2);
      
      // 🚨 CRITICAL FIX: Create fresh organization data using CURRENT FORM VALUES
      const currentFormValues = form.getFieldsValue();
      const freshMultiCmtMembers = currentFormValues.multiCmtMembers || {};
      
      // Extract fresh single CMT members if needed for backwards compatibility
      const freshCmtMembers = [];
      Object.values(freshMultiCmtMembers).forEach(cmtMemberArray => {
        if (Array.isArray(cmtMemberArray)) {
          // 🚨 CRITICAL FIX: Filter out null/undefined members
          const validMembers = cmtMemberArray.filter(member => 
            member && member.email && member.email.trim()
          );
          freshCmtMembers.push(...validMembers);
        }
      });
      
      const freshOrgData = {
        ...organizationData,
        multiCmtMembers: freshMultiCmtMembers,
        cmtMembers: freshCmtMembers
      };
      
      // Trigger ROBUST sync with fresh data using server-side function
      console.log('🚀 EXECUTING ROBUST AUTO SYNC: About to call syncCmtMembers Cloud Function');
      console.log('📊 Sync parameters:', {
        orgId: organizationData.id,
        memberCount: members.length,
        cmtCount: cmts.length
      });
      
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions(undefined, 'australia-southeast2');
      const syncCmtMembersCallable = httpsCallable(functions, 'syncCmtMembers');
      
      // Execute sync for each CMT individually
      const syncResults = [];
      for (const cmt of cmts) {
        const cmtMembers = freshMultiCmtMembers[cmt.id] || [];
        
        if (cmtMembers.length > 0) {
          console.log(`🔄 Auto-syncing ${cmtMembers.length} members for CMT: ${cmt.name} (${cmt.id})`);
          
          const result = await syncCmtMembersCallable({
            orgId: organizationData.id,
            cmtId: cmt.id,
            authoritativeMembers: cmtMembers
          });
          
          syncResults.push({
            cmtId: cmt.id,
            cmtName: cmt.name,
            result: result.data
          });
        }
      }
      
      // Aggregate results
      const aggregatedResult = {
        success: syncResults.every(r => r.result.success),
        totalCmts: syncResults.length,
        results: syncResults,
        totalCreated: syncResults.reduce((sum, r) => sum + (r.result.results?.created || 0), 0),
        totalUpdated: syncResults.reduce((sum, r) => sum + (r.result.results?.updated || 0), 0),
        totalRemoved: syncResults.reduce((sum, r) => sum + (r.result.results?.removed || 0), 0),
        totalUnchanged: syncResults.reduce((sum, r) => sum + (r.result.results?.unchanged || 0), 0)
      };
      
      console.log('✅ ROBUST AUTO SYNC COMPLETED:', aggregatedResult);
      
      setSyncResults(aggregatedResult);
      setLastSyncTime(new Date());
      
      if (aggregatedResult.success) {
        const { totalCreated, totalUpdated } = aggregatedResult;
        if (totalCreated > 0 || totalUpdated > 0) {
          message.success(`Sync complete: ${totalCreated} users created, ${totalUpdated} updated`);
        } else {
          message.info('Sync complete: No changes needed');
        }
      } else {
        const totalErrors = aggregatedResult.results.reduce((sum, r) => sum + (r.result.results?.errors?.length || 0), 0);
        message.warning(`Sync completed with ${totalErrors} errors - check Users tab`);
      }
      
    } catch (error) {
      console.error('❌ Error during auto sync:', error);
      message.error(`Sync failed: ${error.message}`);
    }
  };

  /**
   * 🧪 Handle Manual Dry-Run Sync
   */
  const handleDryRunSync = async () => {
    try {
      setSyncing(true);
      
      console.log('🧪 DRY RUN SYNC: Preview CMT → Users sync...');
      
      // 🔍 DEBUG: Get current form data (fresh) vs stale organizationData
      const currentFormValues = form.getFieldsValue();
      console.log('🔍 DRY RUN DEBUG - Form vs Organization data comparison:', {
        orgId: organizationData.id,
        currentFormValues: currentFormValues,
        staleOrganizationData: {
          multiCmtMembers: organizationData.multiCmtMembers,
          cmtMembers: organizationData.cmtMembers
        },
        cmtsStructure: cmts
      });
      
      // 🚨 FIX: Create fresh organization data using current members state (not stale props)
      // Convert current members state to multiCmtMembers format
      const freshMultiCmtMembers = {};
      members.forEach(member => {
        if (!freshMultiCmtMembers[member.cmtId]) {
          freshMultiCmtMembers[member.cmtId] = [];
        }
        freshMultiCmtMembers[member.cmtId].push({
          fullName: member.fullName,
          email: member.email,
          phone: member.phone,
          cmtRole: member.cmtRole
        });
      });
      
      const freshOrgData = {
        ...organizationData,
        // Use fresh data from current members state, NOT stale parent props
        multiCmtMembers: freshMultiCmtMembers,
        cmtMembers: members
      };
      
      console.log('🔧 Using fresh organization data for sync:', {
        freshMultiCmtMembers: freshOrgData.multiCmtMembers,
        freshCmtMembers: freshOrgData.cmtMembers
      });
      
      // Use new robust sync function for dry-run preview
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions(undefined, 'australia-southeast2');
      const syncCmtMembersCallable = httpsCallable(functions, 'syncCmtMembers');
      
      // Execute dry-run for each CMT individually
      const dryRunResults = [];
      for (const cmt of cmts) {
        const cmtMembers = freshMultiCmtMembers[cmt.id] || [];
        
        if (cmtMembers.length > 0) {
          console.log(`🧪 Dry-run preview for CMT: ${cmt.name} (${cmt.id})`);
          
          const result = await syncCmtMembersCallable({
            orgId: organizationData.id,
            cmtId: cmt.id,
            authoritativeMembers: cmtMembers,
            dryRun: true
          });
          
          dryRunResults.push({
            cmtId: cmt.id,
            cmtName: cmt.name,
            result: result.data
          });
        }
      }
      
      // Aggregate dry-run results to match old format
      const syncResult = {
        success: dryRunResults.every(r => r.result.success),
        dryRun: true,
        stats: {
          totalMembers: dryRunResults.reduce((sum, r) => sum + (r.result.results?.created || 0) + (r.result.results?.updated || 0) + (r.result.results?.unchanged || 0), 0),
          usersCreated: dryRunResults.reduce((sum, r) => sum + (r.result.results?.created || 0), 0),
          usersUpdated: dryRunResults.reduce((sum, r) => sum + (r.result.results?.updated || 0), 0),
          errorsCount: dryRunResults.reduce((sum, r) => sum + (r.result.results?.errors?.length || 0), 0)
        },
        operations: {
          usersToCreate: dryRunResults.flatMap(r => r.result.results?.preview?.toCreate || []),
          usersToUpdate: dryRunResults.flatMap(r => r.result.results?.preview?.toUpdate || []),
          usersToRemove: dryRunResults.flatMap(r => r.result.results?.preview?.toRemove || []),
          usersUnchanged: dryRunResults.flatMap(r => r.result.results?.preview?.unchanged || [])
        },
        cmtResults: dryRunResults
      };
      
      setSyncResults(syncResult);
      
      const { totalMembers, usersCreated, usersUpdated, errorsCount } = syncResult.stats;
      
      message.info(
        `Dry Run Results: ${totalMembers} members would create ${usersCreated} new users and update ${usersUpdated} existing users${errorsCount > 0 ? ` (${errorsCount} errors)` : ''}`
      );
      
    } catch (error) {
      console.error('❌ Error during dry run sync:', error);
      message.error(`Dry run failed: ${error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  /**
   * 🧹 Clean CMT data for safe database save (same as manual save)
   */
  const cleanCmtForAutoSave = (cmt) => {
    const clone = { ...cmt }; // Smart clone that preserves Firestore Timestamps and Dates
    
    // Handle nested objects (permissions, metadata) with shallow clone to avoid reference issues
    if (clone.permissions && typeof clone.permissions === 'object') {
      clone.permissions = { ...clone.permissions };
    }
    if (clone.metadata && typeof clone.metadata === 'object') {
      clone.metadata = { ...clone.metadata };
    }
    if (clone.childCmtIds && Array.isArray(clone.childCmtIds)) {
      clone.childCmtIds = [...clone.childCmtIds];
    }
    
    // Remove ONLY React Flow contamination
    delete clone.onEdit;
    delete clone.onDelete;
    delete clone.onAddChild;
    delete clone.showEditControls;

    // Fix field name mismatch: parentId → parentCmtId
    if (clone.parentId && !clone.parentCmtId) {
      clone.parentCmtId = clone.parentId;
      delete clone.parentId;
    }

    // Ensure required fields exist with sensible defaults
    const requiredFields = {
      'permissions': {},
      'metadata': {},
      'childCmtIds': [],
      'isGlobalCmt': false,
      'isDefault': false,
      'isActive': true,
      'members': []
    };

    Object.entries(requiredFields).forEach(([field, defaultValue]) => {
      if (!(field in clone)) {
        console.warn(`Missing required field: ${field} in CMT ${clone.id}, adding default`);
        clone[field] = defaultValue;
      }
    });
    return clone;
  };

  /**
   * ⚡ Auto-save with debouncing (3 second delay)
   */
  const triggerAutoSave = useCallback(() => {
    // Clear any existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    // Set new timeout for auto-save
    autoSaveTimeoutRef.current = setTimeout(async () => {
      if (!hasChanges) return;
      
      try {
        setAutoSaving(true);
        console.log('🔄 AUTO-SAVE - About to save data:', {
          orgId: organizationData.id,
          cmtCount: cmts.length,
          memberCount: members.length,
          membersData: members,
          cmtsData: cmts.map(cmt => ({ id: cmt.id, name: cmt.name }))
        });
        
        // Clean CMTs before auto-saving (same as manual save)
        const cleanedCmts = cmts.map(cleanCmtForAutoSave);
        
        await cmtService.saveCMTData(
          organizationData.id,
          cleanedCmts,
          members,
          'auto_save'
        );
        
        setHasChanges(false);
        setLastSaved(new Date());
        console.log('✅ Auto-save completed');
        
      } catch (error) {
        console.error('❌ Auto-save failed:', error);
        // Don't show error message for auto-save failures to avoid spam
      } finally {
        setAutoSaving(false);
      }
    }, 3000); // 3 second delay
  }, [hasChanges, organizationData?.id, cmts, members]);

  /**
   * 🔄 Refresh data
   */
  const handleRefresh = () => {
    if (hasChanges) {
      message.warning('You have unsaved changes. Save or they will be lost.');
    }
    loadData();
  };

  /**
   * 🧹 Cleanup auto-save timeout on unmount
   */
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading CMT configuration...</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
      {/* Header */}
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <ApartmentOutlined style={{ marginRight: '8px' }} />
              CMT Management
            </Title>
            <div style={{ marginTop: '8px', color: '#666' }}>
              Organization: <strong>{organizationData?.name}</strong>
            </div>
          </div>
          
          <Space>
            <Button 
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              disabled={saving}
            >
              Refresh
            </Button>
            
            {/* Preview Sync button removed - using integrated Save & Sync */}
            {/* Auto-save status indicator */}
            {autoSaving && (
              <span style={{ color: '#1890ff', fontSize: '14px' }}>
                <SyncOutlined spin /> Auto-saving...
              </span>
            )}
            
            {!autoSaving && lastSaved && !hasChanges && (
              <span style={{ color: '#52c41a', fontSize: '14px' }}>
                <CheckCircleOutlined /> Auto-saved {lastSaved.toLocaleTimeString()}
              </span>
            )}
            
            {hasChanges && !autoSaving && (
              <span style={{ color: '#faad14', fontSize: '14px' }}>
                <ClockCircleOutlined /> Auto-save in 3s...
              </span>
            )}
            
            {/* Last sync indicator */}
            {lastSyncTime && (
              <span style={{ color: '#722ed1', fontSize: '14px' }}>
                <SyncOutlined /> Synced {lastSyncTime.toLocaleTimeString()}
              </span>
            )}
            
                        <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={!hasChanges}
              title="Save changes and sync to Users tab (Firestore-only sync - no Firebase Auth disruption)"
            >
              Save & Sync
            </Button>
            
            <Button onClick={onClose}>
              Close
            </Button>
          </Space>
        </div>
        
        {hasChanges && (
          <Alert
            message="You have unsaved changes - they will auto-save in 3 seconds or use 'Save CMT Changes' button above"
            type="warning"
            showIcon
            style={{ marginTop: '16px' }}
          />
        )}
      </Card>

      {/* Sync Results Display */}
      {syncResults && (
        <Card style={{ marginBottom: '24px' }}>
          <Title level={5}>
            <SyncOutlined /> Sync Results {syncResults.dryRun ? '(Preview)' : '(Live)'}
          </Title>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
            <div>
              <strong>Total Members:</strong> {syncResults?.stats?.totalMembers || 0}
            </div>
            <div>
              <strong>Users to Create:</strong> {syncResults?.stats?.usersCreated || 0}
            </div>
            <div>
              <strong>Users to Update:</strong> {syncResults?.stats?.usersUpdated || 0}
            </div>
            <div>
              <strong>Errors:</strong> {syncResults?.stats?.errorsCount || 0}
            </div>
          </div>
          
          {syncResults?.emailValidation && !syncResults.emailValidation.isValid && (
            <Alert
              message="Email Validation Issues"
              description={
                <div>
                  <div>Conflicts: {syncResults.emailValidation.conflicts.length}</div>
                  <div>Errors: {syncResults.emailValidation.errors.length}</div>
                  {syncResults.emailValidation.conflicts.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      <strong>Conflicting emails:</strong>
                      <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                        {syncResults.emailValidation.conflicts.slice(0, 3).map((conflict, index) => (
                          <li key={index}>{conflict.member.email} (already in another org)</li>
                        ))}
                        {syncResults.emailValidation.conflicts.length > 3 && (
                          <li>...and {syncResults.emailValidation.conflicts.length - 3} more</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              }
              type="warning"
              showIcon
              style={{ marginTop: '12px' }}
            />
          )}
          
          {syncResults?.operations?.errors?.length > 0 && (
            <Alert
              message="Sync Errors"
              description={
                <div>
                  <strong>Failed operations:</strong>
                  <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                    {(syncResults?.operations?.errors || []).slice(0, 3).map((error, index) => (
                      <li key={index}>{error.email}: {error.error}</li>
                    ))}
                    {(syncResults?.operations?.errors?.length || 0) > 3 && (
                      <li>...and {(syncResults?.operations?.errors?.length || 0) - 3} more errors</li>
                    )}
                  </ul>
                </div>
              }
              type="error"
              showIcon
              style={{ marginTop: '12px' }}
            />
          )}
          
          {syncResults.success && syncResults.stats.errorsCount === 0 && (
            <Alert
              message={syncResults.dryRun ? "Preview completed successfully" : "Sync completed successfully"}
              description={`All ${syncResults.stats.totalMembers} CMT members processed without errors`}
              type="success"
              showIcon
              style={{ marginTop: '12px' }}
            />
          )}
        </Card>
      )}

      {/* Main Content */}
      <Card>
        <Tabs defaultActiveKey="structure" type="card">
          <TabPane 
            tab={
              <span>
                <ApartmentOutlined />
                Structure Management
              </span>
            } 
            key="structure"
          >
            <CMTStructureManager 
              cmts={cmts}
              onStructureChange={handleStructureChange}
            />
          </TabPane>
          
          <TabPane 
            tab={
              <span>
                <TeamOutlined />
                Member Management
              </span>
            } 
            key="members"
          >
            <Form 
              key={(orgDoc && orgDoc.updatedAt && orgDoc.updatedAt.seconds) || 0}
              form={form}
              onValuesChange={(changedValues, allValues) => {
                console.log('🔄 FORM VALUES CHANGED:', { changedValues, hasChanges });
                if (!hasChanges) {
                  console.log('✅ Setting hasChanges to TRUE');
                  setHasChanges(true);
                }
              }}
            >
              <CrisisManagementTeam 
                form={form}
                isAdminMode={true}
                // Prefer fresh orgDoc for member data if available to avoid stale props
                organizationData={{...organizationData, ...(orgDoc || {}), cmts}}
                onSave={handleSave}
                onMembersChange={handleMembersChange}
              />
            </Form>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
}
